import React, { useState } from 'react';
import { Card, Form, Button, Row, Col } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const MAX_ROWS = 4;

const modules = {
  toolbar: {
    container: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean'],
    ],
  },
};

const formats = [
  'bold', 'italic', 'underline', 'list', 'link',
];

// Single rich-text card column
const RichTextCard = ({ value, onChange, onDelete, showDelete }) => {
  return (
    <Card
      style={{
        background: '#fff',
        border: '1.5px solid #1890ff',
        borderRadius: 8,
        height: '100%',
      }}
      bodyStyle={{ padding: '12px' }}
      extra={
        showDelete && (
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={onDelete}
          />
        )
      }
    >
      <style>{`
        .ql-editor { min-height: 200px; }
        .ql-container { font-size: 14px; }
      `}</style>
      <ReactQuill
        theme="snow"
        value={value || ''}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder="Write something here..."
        style={{ height: '220px', marginBottom: '42px' }}
      />
    </Card>
  );
};

// A single row with 1–4 columns
const RowBlock = ({ rowIndex, columns, onColumnChange, onAddColumn, onDeleteColumn, onDeleteRow, isLastRow }) => {
  const canAddCol = columns.length < 4;

  return (
    <div
      style={{
        border: '2px dashed #1890ff',
        borderRadius: 10,
        padding: '16px',
        background: '#f9fafb',
        marginBottom: 16,
        position: 'relative',
      }}
    >
      {/* Row header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontWeight: 600, color: '#555', fontSize: 13 }}>Row {rowIndex + 1}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          {canAddCol && (
            <Button
              type="dashed"
              size="small"
              icon={<PlusOutlined />}
              onClick={onAddColumn}
              style={{ borderColor: '#1890ff', color: '#1890ff' }}
            >
              Add Column
            </Button>
          )}
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={onDeleteRow}
          >
            Remove Row
          </Button>
        </div>
      </div>

      {/* Columns */}
      <Row gutter={[12, 12]}>
        {columns.map((col, colIndex) => (
          <Col key={col.id} span={Math.floor(24 / columns.length)}>
            <RichTextCard
              value={col.content}
              onChange={(val) => onColumnChange(colIndex, val)}
              onDelete={() => onDeleteColumn(colIndex)}
              showDelete={columns.length > 1}
            />
          </Col>
        ))}
      </Row>
    </div>
  );
};

// Unique ID generator
let _id = 0;
const uid = () => `col_${++_id}_${Date.now()}`;

const Layout11 = ({ onChange, initialData }) => {
  const buildInitialRows = () => {
    if (initialData?.rows && Array.isArray(initialData.rows)) {
      return initialData.rows.map((row) => ({
        id: uid(),
        columns: row.columns.map((col) => ({ id: uid(), content: col.content || '' })),
      }));
    }
    // Default: 1 row, 1 column
    return [{ id: uid(), columns: [{ id: uid(), content: initialData?.content || '' }] }];
  };

  const [rows, setRows] = useState(buildInitialRows);

  const notifyChange = (updatedRows) => {
    if (onChange) {
      onChange({
        rows: updatedRows.map((row) => ({
          columns: row.columns.map((col) => ({ content: col.content })),
        })),
      });
    }
  };

  const handleColumnChange = (rowIndex, colIndex, value) => {
    const updated = rows.map((row, ri) => {
      if (ri !== rowIndex) return row;
      return {
        ...row,
        columns: row.columns.map((col, ci) =>
          ci === colIndex ? { ...col, content: value } : col
        ),
      };
    });
    setRows(updated);
    notifyChange(updated);
  };

  const handleAddColumn = (rowIndex) => {
    const updated = rows.map((row, ri) => {
      if (ri !== rowIndex || row.columns.length >= 4) return row;
      return { ...row, columns: [...row.columns, { id: uid(), content: '' }] };
    });
    setRows(updated);
    notifyChange(updated);
  };

  const handleDeleteColumn = (rowIndex, colIndex) => {
    const updated = rows.map((row, ri) => {
      if (ri !== rowIndex) return row;
      return { ...row, columns: row.columns.filter((_, ci) => ci !== colIndex) };
    });
    setRows(updated);
    notifyChange(updated);
  };

  const handleAddRow = () => {
    if (rows.length >= MAX_ROWS) return;
    const updated = [...rows, { id: uid(), columns: [{ id: uid(), content: '' }] }];
    setRows(updated);
    notifyChange(updated);
  };

  const handleDeleteRow = (rowIndex) => {
    const updated = rows.filter((_, ri) => ri !== rowIndex);
    const final = updated.length === 0
      ? [{ id: uid(), columns: [{ id: uid(), content: '' }] }]
      : updated;
    setRows(final);
    notifyChange(final);
  };

  return (
    <div style={{ padding: '16px' }}>
      {rows.map((row, rowIndex) => (
        <RowBlock
          key={row.id}
          rowIndex={rowIndex}
          columns={row.columns}
          onColumnChange={(colIndex, val) => handleColumnChange(rowIndex, colIndex, val)}
          onAddColumn={() => handleAddColumn(rowIndex)}
          onDeleteColumn={(colIndex) => handleDeleteColumn(rowIndex, colIndex)}
          onDeleteRow={() => handleDeleteRow(rowIndex)}
        />
      ))}

      {rows.length < MAX_ROWS && (
        <Button
          type="dashed"
          block
          icon={<PlusOutlined />}
          onClick={handleAddRow}
          style={{
            borderColor: '#1890ff',
            color: '#1890ff',
            height: 44,
            fontSize: 14,
            borderRadius: 8,
          }}
        >
          Add Row (max {MAX_ROWS})
        </Button>
      )}

      {rows.length >= MAX_ROWS && (
        <div style={{ textAlign: 'center', color: '#aaa', fontSize: 13, marginTop: 8 }}>
          Maximum of {MAX_ROWS} rows reached.
        </div>
      )}
    </div>
  );
};

export default Layout11;